import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { masterApi, apiError, formatPhone, onlyDigits } from "@/lib/api";
import { ExcelBtn } from "@/lib/excelDownload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { Search, Pencil, Trash2, Save, Car, Plus, Filter, Clock, UserX, Repeat, TrendingDown, Send, XCircle, Unlock, ShieldAlert, Gift, Copy, ExternalLink, Users2 } from "lucide-react";
import PageHeader from "@/components/crud/PageHeader";
import Pagination from "@/components/crud/Pagination";
import { normalizaPaginado } from "@/lib/paginacao";
import { codigoExpirado } from "@/lib/rotulos";

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";
const chipFiltro = "inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[11.5px] font-medium border border-[var(--line)] bg-white text-neutral-700 hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)] hover:bg-blue-50/40 transition-colors";
const stepperWrap = "inline-flex items-center gap-1.5 h-9 px-2.5 rounded-full bg-neutral-50 border border-[var(--line)]";
const stepperInput = "h-6 w-10 bg-transparent text-center text-xs font-semibold text-[color:var(--ink)] outline-none";
const PAGE_SIZE = 50;

export default function MasterClientes() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dias, setDias] = useState(30);
  const [minAbast, setMinAbast] = useState(4);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [insightMode, setInsightMode] = useState(false);
  const [insightTipo, setInsightTipo] = useState(null); // filtro inteligente ativo (para paginar)
  const [creating, setCreating] = useState(false);
  const [indicacaoCli, setIndicacaoCli] = useState(null);
  // v2.29: grupos de consumo (nome no cadastro + filtro vindo de Grupos de Consumo › "N clientes")
  const [grupos, setGrupos] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const grupoFiltro = searchParams.get("grupo") || "";
  const grupoNome = (id) => grupos.find((g) => g.id === id)?.nome;
  useEffect(() => {
    masterApi.get("/grupos-consumo").then((r) => setGrupos(r.data.grupos || [])).catch(() => setGrupos([]));
  }, []);

  const load = async (targetPage = 1) => {
    try {
      const params = new URLSearchParams({ page: String(targetPage), page_size: String(PAGE_SIZE) });
      if (q) params.set("q", q);
      if (grupoFiltro) params.set("grupo_consumo_id", grupoFiltro);
      const { data } = await masterApi.get(`/clientes?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
      setPage(data.page || targetPage);
      setInsightMode(false);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(1); }, [grupoFiltro]);

  // Item 1 (19/10): filtros inteligentes paginados (?page). Backend antigo devolve a lista toda
  // (uma página só, como antes).
  const loadInsight = async (tipo, targetPage = 1) => {
    const params = new URLSearchParams({ tipo });
    if (tipo === "sumidos") params.set("dias", String(dias));
    if (tipo === "frequentes" || tipo === "low_freq") params.set("min_abast", String(minAbast));
    params.set("page", String(targetPage));
    params.set("page_size", String(PAGE_SIZE));
    try {
      const { data } = await masterApi.get(`/master/clientes-insights?${params.toString()}`);
      const pg = normalizaPaginado(data, targetPage);
      setItems(pg.items);
      setTotal(pg.total);
      setTotalPages(pg.total_pages);
      setPage(pg.page);
      setInsightTipo(tipo);
      setInsightMode(true);
    } catch (e) { modal.error(apiError(e)); }
  };

  const onDel = async (id) => {
    const ok = await modal.confirm({
      title: "Excluir cliente",
      message: "O cliente e seu histórico de fidelidade serão removidos. Esta ação não pode ser desfeita.",
      confirmText: "Excluir",
      tone: "danger",
    });
    if (!ok) return;
    try { await masterApi.delete(`/clientes/${id}`); modal.success("Cliente excluído com sucesso."); load(page); }
    catch (e) { modal.error(apiError(e)); }
  };

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-clientes">
      <PageHeader
        chip="Base de cadastros"
        title="Clientes"
        user="Master"
        actions={
          <>
            <button data-testid="novo-cliente-btn" onClick={() => setCreating(true)}
                    className="btn-action btn-novo">
              <Plus size={16} /> Novo
            </button>
            <ExcelBtn testid="excel-clientes" url={`/master/clientes/excel${q ? `?q=${encodeURIComponent(q)}` : ""}`}
                      filename="clientes.xlsx" label="Excel" />
          </>
        }
      />

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
          <input data-testid="clientes-search" value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load(1)}
                 placeholder="Buscar por nome, telefone, CPF ou placa"
                 className="w-full h-11 rounded-xl bg-white border border-neutral-200 pl-10 pr-4 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition" />
        </div>
        <button data-testid="clientes-search-btn" onClick={() => load(1)}
                className="btn-primary-blue h-11 px-5 text-[12px] font-medium flex items-center gap-1.5 shrink-0">
          <Search size={14} /> Buscar
        </button>
      </div>

      <div className="card-soft rounded-2xl p-3.5" data-testid="clientes-filtros">
        <div className="flex items-center gap-1.5 mb-3">
          <Filter size={13} className="text-[color:var(--brand-blue)]" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">Filtros inteligentes</span>
          {insightMode && (
            <button onClick={() => load(1)} data-testid="insight-clear"
                    className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-[color:var(--brand-blue)] hover:underline">
              <XCircle size={12} /> Limpar filtros
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="insight-sumidos" onClick={() => loadInsight("sumidos")} className={chipFiltro}>
            <Clock size={12} /> Sumidos {dias}d
          </button>
          <div className={stepperWrap}>
            <span className="text-[9px] uppercase tracking-wider text-neutral-400">dias</span>
            <input data-testid="insight-dias" type="number" value={dias} onChange={(e) => setDias(parseInt(e.target.value) || 30)} className={stepperInput} />
          </div>
          <button data-testid="insight-primeiro" onClick={() => loadInsight("primeiro_nao_voltou")} className={chipFiltro}>
            <UserX size={12} /> 1º cad. não voltou
          </button>
          <button data-testid="insight-frequentes" onClick={() => loadInsight("frequentes")} className={chipFiltro}>
            <Repeat size={12} /> Frequentes ≥
          </button>
          <div className={stepperWrap}>
            <span className="text-[9px] uppercase tracking-wider text-neutral-400">mín</span>
            <input data-testid="insight-min" type="number" value={minAbast} onChange={(e) => setMinAbast(parseInt(e.target.value) || 4)} className={stepperInput} />
          </div>
          <button data-testid="insight-lowfreq" onClick={() => loadInsight("low_freq")} className={chipFiltro}>
            <TrendingDown size={12} /> Baixa Freq. &lt;
          </button>
          <button data-testid="insight-saudade" onClick={() => loadInsight("saudade_enviado")} className={chipFiltro}>
            <Send size={12} /> Saudade enviado
          </button>
        </div>
      </div>

      {grupoFiltro && (
        <div className="flex items-center gap-2 text-[12px]" data-testid="clientes-filtro-grupo">
          <span className="chip text-[11px] py-1"><Users2 size={12} /> Grupo de consumo: <b>{grupoNome(grupoFiltro) || "—"}</b></span>
          <button onClick={() => setSearchParams({})} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[color:var(--brand-blue)] hover:underline">
            <XCircle size={12} /> Mostrar todos
          </button>
        </div>
      )}

      <div className="flex items-center justify-between text-[11px] text-neutral-600 px-1">
        <span data-testid="clientes-total">
          {insightMode ? `${total} resultado${total !== 1 ? "s" : ""} (insight)` : `${total} cadastrado${total !== 1 ? "s" : ""}`}
        </span>
        {totalPages > 1 && (
          <span>Página {page} de {totalPages}</span>
        )}
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Nome</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Categoria</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Telefone</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Placa</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Abast.</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Último</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Fidel</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Cashback</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`cliente-row-${c.id}`}>
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell>
                  {c.categoria === "app_driver"
                    ? <span className="chip text-[10px] py-0.5" style={{ background: "rgba(217,119,6,0.18)", color: "#92400E" }}><Car size={11} />App Driver</span>
                    : <span className="chip text-[10px] py-0.5">Normal</span>}
                  {c.grupo_consumo_id && grupoNome(c.grupo_consumo_id) && (
                    <div className="mt-1"><span className="chip chip-gray text-[10px] py-0.5" data-testid={`cliente-grupo-${c.id}`}>
                      <Users2 size={11} />{grupoNome(c.grupo_consumo_id)}
                    </span></div>
                  )}
                </TableCell>
                <TableCell>{formatPhone(c.telefone)}</TableCell>
                <TableCell>{c.placa}</TableCell>
                <TableCell>{c.total_abastecimentos || 0}</TableCell>
                <TableCell className="text-xs">{c.ultimo_abastecimento_em ? new Date(c.ultimo_abastecimento_em).toLocaleDateString("pt-BR") : "—"}</TableCell>
                <TableCell>{(c.litros_acumulados_fidelidade || 0).toFixed(0)}L</TableCell>
                <TableCell>R$ {(c.saldo_cashback || 0).toFixed(2)}</TableCell>
                <TableCell className="text-right space-x-1 whitespace-nowrap">
                  <button data-testid={`toggle-cat-${c.id}`} title={c.categoria === "app_driver" ? "Rebaixar para Normal" : "Promover para Motorista de App"}
                          onClick={async () => {
                            const nova = c.categoria === "app_driver" ? "normal" : "app_driver";
                            try {
                              const { data: r } = await masterApi.patch(`/app_driver/cliente/${c.id}/categoria`, { categoria: nova });
                              modal.success(`Cliente movido para ${nova === "app_driver" ? "Motorista de App" : "Normal"}`
                                + (r?.grupo_desvinculado ? `. Saiu do grupo de consumo "${r.grupo_desvinculado}" (o grupo é da outra categoria).` : ""));
                              load(page);
                            } catch (e) { modal.error(apiError(e)); }
                          }}
                          className="icon-action icon-action--amber"><Car size={14} /></button>
                  <button data-testid={`indicacao-cliente-${c.id}`} title="Gerar indicação / link WhatsApp"
                          onClick={() => setIndicacaoCli(c)}
                          className="icon-action icon-action--amber"><Gift size={14} /></button>
                  <button data-testid={`edit-cliente-${c.id}`} onClick={() => setEditing(c)}
                          className="icon-action icon-action--edit"><Pencil size={14} /></button>
                  <button data-testid={`del-cliente-${c.id}`} onClick={() => onDel(c.id)}
                          className="icon-action icon-action--danger"><Trash2 size={14} /></button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-neutral-500 py-6">Nenhum cliente.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Pagination page={page} totalPages={totalPages} total={total}
                  onPage={insightMode ? (p) => loadInsight(insightTipo, p) : load} unitLabel="clientes" />

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md rounded-2xl border-none p-0">
          {editing && (
            <div className="bg-white p-5 rounded-2xl">
              <EditCliente cliente={editing} grupos={grupos} onDone={() => { setEditing(null); load(); }} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-md rounded-2xl border-none p-0">
          <div className="bg-white p-5 rounded-2xl">
            <NewCliente grupos={grupos} onDone={() => { setCreating(false); load(1); }} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!indicacaoCli} onOpenChange={(v) => !v && setIndicacaoCli(null)}>
        <DialogContent className="max-w-md rounded-2xl border-none p-0">
          <div className="bg-white p-5 rounded-2xl">
            {indicacaoCli && <IndicacaoMaster cliente={indicacaoCli} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IndicacaoMaster({ cliente }) {
  const [codigos, setCodigos] = useState([]);
  const [gerado, setGerado] = useState(null); // { codigos, whatsapp_link, mensagem }
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await masterApi.get(`/master/indicacao/codigos/${cliente.id}`);
      setCodigos(data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [cliente.id]);

  const gerar = async () => {
    setLoading(true);
    try {
      const { data } = await masterApi.post(`/master/indicacao/gerar/${cliente.id}`, {});
      setGerado(data);
      await load();
    } catch (e) { modal.error(apiError(e)); }
    finally { setLoading(false); }
  };
  const copiar = (t) => { navigator.clipboard?.writeText(t); modal.success("Copiado"); };

  return (
    <div className="space-y-3" data-testid="indicacao-master">
      <DialogHeader>
        <DialogTitle className="font-serif-display text-xl flex items-center gap-2">
          <Gift size={17} className="text-amber-600" /> Indicação — {cliente.nome}
        </DialogTitle>
      </DialogHeader>

      <button data-testid="indicacao-gerar" onClick={gerar} disabled={loading}
              className="btn-action btn-novo w-full justify-center">
        <Plus size={15} /> {loading ? "Gerando..." : "Gerar 3 novos códigos"}
      </button>

      {gerado && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 space-y-2">
          <div className="text-[11px] text-neutral-600">Códigos gerados. Envie para o cliente:</div>
          <div className="flex flex-wrap gap-1.5">
            {gerado.codigos.map((c) => (
              <span key={c} className="font-mono text-sm font-semibold bg-white border border-amber-200 rounded px-2 py-1">{c}</span>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <a data-testid="indicacao-wa" href={gerado.whatsapp_link} target="_blank" rel="noreferrer"
               className="btn-primary-blue h-10 text-[12px] font-medium flex items-center justify-center gap-1.5">
              <ExternalLink size={13} /> Abrir WhatsApp
            </a>
            <button data-testid="indicacao-copiar" onClick={() => copiar(gerado.mensagem)}
                    className="btn-ghost h-10 text-[12px] font-medium flex items-center justify-center gap-1.5">
              <Copy size={13} /> Copiar mensagem
            </button>
          </div>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">Códigos deste cliente</div>
        <div className="space-y-1 max-h-52 overflow-y-auto">
          {codigos.map((c) => (
            <div key={c.codigo} className="flex items-center justify-between gap-2 text-[12px] border-b border-neutral-100 pb-1">
              <span className="font-mono font-semibold">{c.codigo}</span>
              {c.usado_por
                ? <span className="text-emerald-700 text-[11px]">✓ usado por {c.usado_nome || "cliente"}</span>
                : codigoExpirado(c)
                  ? <span className="text-rose-600 text-[11px]" data-testid={`codigo-expirado-${c.codigo}`}>expirado</span>
                  : <span className="text-neutral-400 text-[11px]">disponível</span>}
            </div>
          ))}
          {codigos.length === 0 && <div className="text-[12px] text-neutral-400 py-2">Nenhum código gerado ainda.</div>}
        </div>
      </div>
    </div>
  );
}

// Máscaras BR (só display) — o submit usa onlyDigits/upper, então o salvo fica limpo.
const maskTel = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};
const maskCPF = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};
const maskPlaca = (v) => (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);

function NewCliente({ grupos = [], onDone }) {
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [cpf, setCpf] = useState("");
  const [placa, setPlaca] = useState("");
  const [categoria, setCategoria] = useState("normal");
  const [grupoId, setGrupoId] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!nome.trim() || !telefone.trim() || !placa.trim()) return modal.error("Preencha nome, telefone e placa");
    setSaving(true);
    try {
      await masterApi.post(`/clientes`, { nome, telefone: onlyDigits(telefone), cpf: cpf ? onlyDigits(cpf) : null, placa, categoria,
                                          grupo_consumo_id: grupoId || null });
      modal.success("Cliente cadastrado"); onDone();
    } catch (e) { modal.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-3">
      <DialogHeader><DialogTitle className="font-display font-black uppercase text-2xl">Novo Cliente</DialogTitle></DialogHeader>
      <Field label="Nome"><input data-testid="new-nome" value={nome} onChange={(e) => setNome(e.target.value)} className={inputCls} /></Field>
      <Field label="Telefone"><input data-testid="new-telefone" value={telefone} onChange={(e) => setTelefone(maskTel(e.target.value))} inputMode="numeric" maxLength={16} className={inputCls} placeholder="(51) 99999-9999" /></Field>
      <Field label="CPF (opcional)"><input data-testid="new-cpf" value={cpf} onChange={(e) => setCpf(maskCPF(e.target.value))} inputMode="numeric" maxLength={14} className={inputCls} placeholder="000.000.000-00" /></Field>
      <Field label="Placa"><input data-testid="new-placa" value={placa} onChange={(e) => setPlaca(maskPlaca(e.target.value))} maxLength={7} className={inputCls + " uppercase"} placeholder="ABC1D23" /></Field>
      <Field label="Categoria">
        <select data-testid="new-categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)} className={inputCls}>
          <option value="normal">Normal</option>
          <option value="app_driver">Motorista de App</option>
        </select>
      </Field>
      <GrupoSelect grupos={grupos} categoria={categoria} value={grupoId} onChange={setGrupoId} testid="new-grupo" />
      <button data-testid="new-save" onClick={save} disabled={saving}
              className="btn-action btn-salvar w-full">
        <Save size={15} /> {saving ? "Salvando..." : "Cadastrar"}
      </button>
    </div>
  );
}

function EditCliente({ cliente, grupos = [], onDone }) {
  const [nome, setNome] = useState(cliente.nome);
  const [grupoId, setGrupoId] = useState(cliente.grupo_consumo_id || "");
  const [telefone, setTelefone] = useState(maskTel(cliente.telefone));
  const [cpf, setCpf] = useState(maskCPF(cliente.cpf || ""));
  const [categoria, setCategoria] = useState(cliente.categoria || "normal");
  const [limite, setLimite] = useState(cliente.limite_acesso_diario || 0);
  const [placas, setPlacas] = useState([]);
  const [novaPlaca, setNovaPlaca] = useState("");
  const [bloqueado, setBloqueado] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const bloq = cliente.bloqueado_ate ? new Date(cliente.bloqueado_ate) > new Date() : false;
    setBloqueado(bloq);
    masterApi.get(`/clientes/${cliente.id}/placas`).then((r) => setPlacas(r.data)).catch(() => setPlacas([]));
  }, [cliente.id]);

  const addPlaca = async () => {
    const pl = maskPlaca(novaPlaca);
    if (pl.length < 5) return modal.error("Placa inválida");
    try {
      const { data } = await masterApi.post(`/clientes/${cliente.id}/placas`, { placa: pl });
      setPlacas(data); setNovaPlaca("");
    } catch (e) { modal.error(apiError(e)); }
  };
  const delPlaca = async (pl) => {
    try {
      const { data } = await masterApi.delete(`/clientes/${cliente.id}/placas/${pl}`);
      setPlacas(data);
    } catch (e) { modal.error(apiError(e)); }
  };
  const desbloquear = async () => {
    try {
      await masterApi.post(`/clientes/${cliente.id}/desbloquear`, {});
      setBloqueado(false); modal.success("Cadastro desbloqueado");
    } catch (e) { modal.error(apiError(e)); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await masterApi.put(`/clientes/${cliente.id}`, {
        nome, telefone: onlyDigits(telefone), cpf: cpf ? onlyDigits(cpf) : null,
        categoria, limite_acesso_diario: Number(limite) || 0,
        grupo_consumo_id: grupoId || null,  // vazio = desvincula
      });
      modal.success("Atualizado"); onDone();
    } catch (e) { modal.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-3">
      <DialogHeader><DialogTitle className="font-serif-display text-xl">Editar Cliente</DialogTitle></DialogHeader>

      {bloqueado && (
        <div data-testid="edit-bloqueado" className="rounded-lg p-2.5 flex items-center justify-between gap-2 border border-rose-200"
             style={{ background: "linear-gradient(135deg,#FEE2E2 0%,#FECACA 100%)" }}>
          <span className="text-[12px] font-medium text-rose-800 flex items-center gap-1.5">
            <ShieldAlert size={14} /> Cadastro bloqueado por tentativas
          </span>
          <button data-testid="edit-desbloquear" onClick={desbloquear}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-rose-600 text-white text-[11px] font-semibold hover:bg-rose-700 transition">
            <Unlock size={12} /> Desbloquear
          </button>
        </div>
      )}

      <Field label="Nome"><input data-testid="edit-nome" value={nome} onChange={(e) => setNome(e.target.value)} className={inputCls} /></Field>
      <Field label="Telefone"><input data-testid="edit-telefone" value={telefone} onChange={(e) => setTelefone(maskTel(e.target.value))} inputMode="numeric" maxLength={16} className={inputCls} /></Field>
      <Field label="CPF"><input data-testid="edit-cpf" value={cpf} onChange={(e) => setCpf(maskCPF(e.target.value))} inputMode="numeric" maxLength={14} className={inputCls} /></Field>
      <Field label="Categoria">
        <select data-testid="edit-categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)} className={inputCls}>
          <option value="normal">Normal</option>
          <option value="app_driver">Motorista de App</option>
        </select>
      </Field>
      <GrupoSelect grupos={grupos} categoria={categoria} value={grupoId} onChange={setGrupoId} testid="edit-grupo" />

      <Field label={`Placas (até 3) — ${placas.length}/3`}>
        <div className="space-y-1.5">
          {placas.map((p) => (
            <div key={p.placa} data-testid={`edit-placa-${p.placa}`}
                 className="flex items-center justify-between gap-2 h-10 px-3 rounded-lg bg-neutral-50 border border-neutral-200">
              <span className="font-mono text-sm font-semibold uppercase">{p.placa}
                {p.principal && <span className="ml-2 text-[9px] uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">principal</span>}
              </span>
              <button data-testid={`del-placa-${p.placa}`} onClick={() => delPlaca(p.placa)} disabled={placas.length <= 1}
                      className="icon-action icon-action--danger disabled:opacity-30"><Trash2 size={13} /></button>
            </div>
          ))}
          {placas.length < 3 && (
            <div className="flex gap-1.5">
              <input data-testid="edit-nova-placa" value={novaPlaca} onChange={(e) => setNovaPlaca(maskPlaca(e.target.value))}
                     maxLength={7} placeholder="Adicionar placa" className={inputCls + " uppercase"} />
              <button data-testid="edit-add-placa" onClick={addPlaca}
                      className="btn-primary-blue h-10 px-3 text-[12px] font-medium shrink-0 flex items-center gap-1"><Plus size={13} /> Add</button>
            </div>
          )}
        </div>
      </Field>

      <Field label="Limite de acessos por dia (0 = usa o padrão global da Configuração)">
        <input data-testid="edit-limite-diario" type="number" min={0} value={limite}
               onChange={(e) => setLimite(parseInt(e.target.value) || 0)} className={inputCls} />
      </Field>

      <button data-testid="edit-save" onClick={save} disabled={saving}
              className="btn-action btn-salvar w-full">
        <Save size={15} /> {saving ? "Salvando..." : "Salvar"}
      </button>
    </div>
  );
}

// v2.29 — vínculo do cliente a um grupo de consumo (vincular / trocar / remover).
// O grupo é subgrupo da categoria: só aparecem grupos da categoria escolhida no formulário.
function GrupoSelect({ grupos, categoria, value, onChange, testid }) {
  const daCategoria = grupos.filter((g) => (g.categoria || "normal") === (categoria || "normal"));
  useEffect(() => {
    if (value && grupos.length && !daCategoria.some((g) => g.id === value)) onChange("");
  }, [categoria, grupos.length]);
  if (!daCategoria.length && !value) return null;
  return (
    <Field label={`Grupo de consumo (${categoria === "app_driver" ? "Motorista de App" : "Cliente Normal"})`}>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">Sem grupo</option>
        {daCategoria.map((g) => (
          <option key={g.id} value={g.id}>
            {g.nome}{g.regra_desconto === "somente_sistema" || !g.tipo_desconto ? " — só descontos do sistema"
              : ` — ${g.tipo_desconto === "percentual"
                ? `${Number(g.valor).toLocaleString("pt-BR")}%`
                : `${Number(g.valor).toLocaleString("pt-BR")}¢/L`}`}{g.ativo ? "" : " (inativo)"}
          </option>
        ))}
      </select>
    </Field>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
